/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Dominik Boeck <d.boeck@techdivision.com>
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import {
  Box, Grid, Tab, Tabs,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import SubscriptionTerms
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Checkout/FormSteps/ContractForm/Components/SubscriptionTerms';
import ContractDescription
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Checkout/FormSteps/ContractForm/Components/ContractDescription';
import PropTypes from 'prop-types';

/**
 * Subscriptions Component
 * @param contractData
 * @param contract
 * @param oldContractTermPrice
 * @param tab
 * @param setTab
 * @returns {JSX.Element}
 * @constructor
 */
export default function Subscriptions({
  contractData, contract, oldContractTermPrice, tab, setTab,
}) {
  const [value, setValue] = React.useState(0);

  /**
     * Handle tabs open / close
     */
  const handleChange = (event, newValue) => {
    setTab(newValue);
    setValue(newValue);
  };

  const getLowestTermPrice = (index) => {
    const { terms } = contractData[index];
    const lowest = terms.reduce((previous, current) => (current.price < previous.price ? current : previous));

    return lowest.price;
  };

  /**
     * @returns {JSX.Element}
     */
  const renderSubscription = (rateBundle, index) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-tabpanel-${index}`}
      aria-labelledby={`contract-tab-${index}`}
      key={`subscription-${uuidv4()}`}
    >
      {value === index && (
      <Box>
        <Grid container sx={{ mb: 2 }} className="package-box">
          <Grid
            item
            xs={12}
            className="package-container"
            data-amount={rateBundle.terms.length > 1 ? 'multiple' : 'single'}
          >
            <SubscriptionTerms
              contract={contract}
              rateBundle={rateBundle}
              oldContractTermPrice={oldContractTermPrice}
            />
          </Grid>
          {contractData ? <ContractDescription contract={contractData[tab]} /> : null}
        </Grid>
      </Box>
      )}
    </div>
  );

  return (
    <Grid item xs={12}>
      <Box className="tab-container">
        <Tabs
          value={value}
          variant="scrollable"
          allowScrollButtonsMobile
          indicatorColor="secondary.contrastText"
          scrollButtons={contractData && contractData.length > 3 ? 'auto' : false}
          onChange={handleChange}
          aria-label="contract tabs"
          className="contact-tabs"
        >
          {contractData.map((rateBundle, index) => (
            <Tab
              value={index}
              label={(
                <>
                  <Box className="title">{rateBundle.name}</Box>
                  <Box className="price">{`ab ${getLowestTermPrice(index)}€`}</Box>
                </>
)}
              id={`contract-tab-${index}`}
              aria-controls={`contract-tabpanel-${index}`}
              key={`contract-tab-${uuidv4()}`}
            />
          ))}
        </Tabs>

        {contractData.map((rateBundle, index) => renderSubscription(rateBundle, index))}
      </Box>
    </Grid>
  );
}

Subscriptions.propTypes = {
  contractData: PropTypes.array.isRequired,
  contract: PropTypes.object.isRequired,
  oldContractTermPrice: PropTypes.string,
  tab: PropTypes.number.isRequired,
  setTab: PropTypes.func.isRequired,
};

Subscriptions.defaultProps = {
  oldContractTermPrice: null,
};
